// path: src/utils/storyProgress.js

import {
    STORY_LEVELS,
    getStoryLevelById,
    getStoryMainLevelCount,
    getTotalStoryLevels,
    isUnderworldLevel,
} from "../data/storyLevels";

export const LS_STORY_PROGRESS_KEY = "mmu_story_progress_v1";
const LS_MAX_LEVEL_KEY = "mmu_max_level";
const LS_BASE_GAME_COMPLETED_KEY = "mmu_base_game_completed_v1";

const STORY_PROGRESS_VERSION = 2;
const STORY_CATALOG_VERSION = 2;

export const TWISTED_UNLOCK_STORY_LEVEL = 13;
export const SHY_UNLOCK_STORY_LEVEL = 14;
export const ANXIOUS_UNLOCK_STORY_LEVEL = 15;
export const HARDCORE_UNLOCK_STORY_LEVEL = 8;

export function createEmptyStoryProgress() {
    return {
        version: STORY_PROGRESS_VERSION,
        catalogVersion: STORY_CATALOG_VERSION,
        completedLevelIds: [],
        hardModeCompletedLevelIds: [],
        highestUnlockedLevelId: 1,
        storyCompleted: false,
        migratedFromLegacy: false,
        legacyAlwaysOnModifiers: false,
        bonusModifiers: [],
        hardModeEnabled: false,
    };
}

/** Admin unlock-all: every Story level complete, Underworld included. */
export function buildAdminUnlockAllStoryProgress() {
    return normalizeStoryProgress({
        completedLevelIds: STORY_LEVELS.map((level) => level.id),
        storyCompleted: true,
        highestUnlockedLevelId: getTotalStoryLevels(),
        migratedFromLegacy: true,
        catalogVersion: STORY_CATALOG_VERSION,
    });
}

/** Merge full Story unlock onto existing progress (keeps Hard Mode / bonuses). */
export function applyUnlockAllStoryLevels(progress = null) {
    return mergeStoryProgress(progress, buildAdminUnlockAllStoryProgress());
}

function uniqueSortedIds(ids) {
    return Array.from(
        new Set(
            (Array.isArray(ids) ? ids : [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id >= 1)
        )
    ).sort((a, b) => a - b);
}

export function deriveHighestUnlockedLevelId(completedLevelIds = []) {
    const completed = uniqueSortedIds(completedLevelIds);
    let highest = 1;

    for (const id of completed) {
        if (id >= highest) {
            highest = Math.min(getTotalStoryLevels(), id + 1);
        }
    }

    if (completed.includes(getTotalStoryLevels())) {
        highest = getTotalStoryLevels();
    }

    return highest;
}

export function deriveHighestHardModeUnlockedLevelId(
    hardModeCompletedLevelIds = []
) {
    return deriveHighestUnlockedLevelId(hardModeCompletedLevelIds);
}

function uniqueModifierKeys(keys = []) {
    return Array.from(
        new Set(
            (Array.isArray(keys) ? keys : [])
                .map((key) => String(key || "").trim())
                .filter(Boolean)
        )
    );
}

function migrateStoryCatalog(raw = {}) {
    const catalogVersion = Number(raw.catalogVersion) || 1;
    const completedLevelIds = uniqueSortedIds(raw.completedLevelIds);
    const bonusModifiers = uniqueModifierKeys(raw.bonusModifiers);

    if (catalogVersion >= STORY_CATALOG_VERSION) {
        return {
            completedLevelIds,
            bonusModifiers,
            catalogVersion: STORY_CATALOG_VERSION,
            remappedUnderworld: false,
        };
    }

    // v1 catalog taught Twisted/Shy in the Overworld (L6/L8) and used
    // Underworld ids 13–16. Keep Overworld completions, grant those
    // modifiers, and replay the new Underworld from L13.
    if (completedLevelIds.includes(6)) {
        bonusModifiers.push("twistedMode");
    }
    if (completedLevelIds.includes(8)) {
        bonusModifiers.push("shyCorgisMode");
    }
    if (completedLevelIds.includes(10)) {
        bonusModifiers.push("hardcoreMode");
    }
    if (completedLevelIds.includes(13) || completedLevelIds.includes(16)) {
        bonusModifiers.push("blitzMode");
    }

    return {
        completedLevelIds: completedLevelIds.filter(
            (id) => id <= getStoryMainLevelCount()
        ),
        bonusModifiers: uniqueModifierKeys(bonusModifiers),
        catalogVersion: STORY_CATALOG_VERSION,
        remappedUnderworld: true,
    };
}

export function normalizeStoryProgress(raw) {
    const empty = createEmptyStoryProgress();

    if (!raw || typeof raw !== "object") {
        return empty;
    }

    const migratedCatalog = migrateStoryCatalog(raw);
    const completedLevelIds = migratedCatalog.completedLevelIds;
    const storyCompleted =
        Boolean(raw.storyCompleted) ||
        completedLevelIds.includes(getStoryMainLevelCount());

    const derivedHighest = deriveHighestUnlockedLevelId(completedLevelIds);
    const storedHighest = Number.isFinite(Number(raw.highestUnlockedLevelId))
        ? Number(raw.highestUnlockedLevelId)
        : derivedHighest;
    const highestUnlockedLevelId = Math.max(
        1,
        Math.min(
            getTotalStoryLevels(),
            migratedCatalog.remappedUnderworld
                ? derivedHighest
                : Math.max(storedHighest, derivedHighest)
        )
    );

    return {
        version: STORY_PROGRESS_VERSION,
        catalogVersion: migratedCatalog.catalogVersion,
        completedLevelIds,
        hardModeCompletedLevelIds: uniqueSortedIds(
            raw.hardModeCompletedLevelIds
        ).filter((id) => id >= 1 && id <= getTotalStoryLevels()),
        highestUnlockedLevelId,
        storyCompleted,
        migratedFromLegacy: Boolean(raw.migratedFromLegacy),
        legacyAlwaysOnModifiers: Boolean(raw.legacyAlwaysOnModifiers),
        bonusModifiers: migratedCatalog.bonusModifiers,
        hardModeEnabled:
            Boolean(raw.hardModeEnabled) && storyCompleted,
    };
}

function readLegacyMaxLevel() {
    try {
        const parsed = Number(window.localStorage.getItem(LS_MAX_LEVEL_KEY));
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    } catch {
        return 1;
    }
}

function readLegacyBaseGameCompleted() {
    try {
        return (
            window.localStorage.getItem(LS_BASE_GAME_COMPLETED_KEY) === "true"
        );
    } catch {
        return false;
    }
}

export function buildMigratedProgressFromLegacy({
    maxLevel = 1,
    baseGameCompleted = false,
} = {}) {
    const progress = createEmptyStoryProgress();
    progress.migratedFromLegacy = true;

    if (baseGameCompleted) {
        progress.completedLevelIds = STORY_LEVELS.filter(
            (level) => !isUnderworldLevel(level.id)
        ).map((level) => level.id);
        progress.storyCompleted = true;
        progress.highestUnlockedLevelId = getStoryMainLevelCount() + 1;
        progress.legacyAlwaysOnModifiers = true;
        progress.bonusModifiers = [
            "twistedMode",
            "shyCorgisMode",
            "hardcoreMode",
            "blitzMode",
        ];
        return normalizeStoryProgress(progress);
    }

    if (maxLevel >= 7) {
        progress.completedLevelIds = [1, 2, 3, 4, 5, 6, 7, 8, 9];
        progress.legacyAlwaysOnModifiers = true;
        return normalizeStoryProgress(progress);
    }

    if (maxLevel >= 4) {
        progress.completedLevelIds = [1, 2, 3, 4];
        progress.legacyAlwaysOnModifiers = true;
        return normalizeStoryProgress(progress);
    }

    // Existing players who had Hardcore/Blitz always available.
    if (maxLevel > 1) {
        progress.completedLevelIds = [];
        progress.legacyAlwaysOnModifiers = true;
        return normalizeStoryProgress(progress);
    }

    return normalizeStoryProgress(progress);
}

export function loadStoryProgress() {
    try {
        const raw = window.localStorage.getItem(LS_STORY_PROGRESS_KEY);

        if (raw) {
            return normalizeStoryProgress(JSON.parse(raw));
        }
    } catch {
        // fall through to migration
    }

    return migrateLegacyProgressionIfNeeded();
}

export function saveStoryProgress(progress) {
    const normalized = normalizeStoryProgress(progress);

    try {
        window.localStorage.setItem(
            LS_STORY_PROGRESS_KEY,
            JSON.stringify(normalized)
        );
    } catch {
        // ignore localStorage failures
    }

    return normalized;
}

export function migrateLegacyProgressionIfNeeded() {
    try {
        const existing = window.localStorage.getItem(LS_STORY_PROGRESS_KEY);

        if (existing) {
            return normalizeStoryProgress(JSON.parse(existing));
        }
    } catch {
        // continue migration
    }

    const migrated = buildMigratedProgressFromLegacy({
        maxLevel: readLegacyMaxLevel(),
        baseGameCompleted: readLegacyBaseGameCompleted(),
    });

    return saveStoryProgress(migrated);
}

export function mergeStoryProgress(localProgress, remoteProgress) {
    const local = normalizeStoryProgress(localProgress);
    const remote = normalizeStoryProgress(remoteProgress);

    const completedLevelIds = uniqueSortedIds([
        ...local.completedLevelIds,
        ...remote.completedLevelIds,
    ]);
    const hardModeCompletedLevelIds = uniqueSortedIds([
        ...local.hardModeCompletedLevelIds,
        ...remote.hardModeCompletedLevelIds,
    ]);

    return normalizeStoryProgress({
        completedLevelIds,
        hardModeCompletedLevelIds,
        storyCompleted: local.storyCompleted || remote.storyCompleted,
        highestUnlockedLevelId: Math.max(
            local.highestUnlockedLevelId,
            remote.highestUnlockedLevelId,
            deriveHighestUnlockedLevelId(completedLevelIds)
        ),
        migratedFromLegacy:
            local.migratedFromLegacy || remote.migratedFromLegacy,
        legacyAlwaysOnModifiers:
            local.legacyAlwaysOnModifiers || remote.legacyAlwaysOnModifiers,
        bonusModifiers: uniqueModifierKeys([
            ...(local.bonusModifiers || []),
            ...(remote.bonusModifiers || []),
        ]),
        hardModeEnabled: Boolean(local.hardModeEnabled || remote.hardModeEnabled),
        catalogVersion: STORY_CATALOG_VERSION,
    });
}

export function isStoryLevelCompleted(levelId, progress = loadStoryProgress()) {
    const normalized = normalizeStoryProgress(progress);
    return normalized.completedLevelIds.includes(Number(levelId));
}

export function isStoryHardModeLevelCompleted(
    levelId,
    progress = loadStoryProgress()
) {
    const normalized = normalizeStoryProgress(progress);
    return normalized.hardModeCompletedLevelIds.includes(Number(levelId));
}

/** Map completion badge: normal clears, or Hard Mode clears when Hard Mode is on. */
export function isStoryLevelCompletedOnActiveTrack(
    levelId,
    progress = loadStoryProgress()
) {
    if (isStoryHardModeActive(progress)) {
        return isStoryHardModeLevelCompleted(levelId, progress);
    }

    return isStoryLevelCompleted(levelId, progress);
}

/** Whether a level is cleared on the active Story track (normal or Hard Mode). */
function hasCompletedLevelOnActiveTrack(levelId, progress = loadStoryProgress()) {
    if (isStoryHardModeActive(progress)) {
        return isStoryHardModeLevelCompleted(levelId, progress);
    }

    return isStoryLevelCompleted(levelId, progress);
}

function isUnderworldStoryLevelUnlocked(levelId, progress = loadStoryProgress()) {
    const id = Number(levelId);
    const normalized = normalizeStoryProgress(progress);

    if (!isUnderworldUnlocked(normalized)) {
        return false;
    }

    if (hasCompletedLevelOnActiveTrack(id, normalized)) {
        return true;
    }

    if (id === 13) {
        return true;
    }

    if (id === 14) {
        return hasCompletedLevelOnActiveTrack(13, normalized);
    }

    if (id === 15) {
        return hasCompletedLevelOnActiveTrack(14, normalized);
    }

    if (id === 16 || id === 17 || id === 18) {
        return hasCompletedLevelOnActiveTrack(15, normalized);
    }

    if (id === 19) {
        return (
            hasCompletedLevelOnActiveTrack(16, normalized) &&
            hasCompletedLevelOnActiveTrack(17, normalized) &&
            hasCompletedLevelOnActiveTrack(18, normalized)
        );
    }

    return false;
}

export function isStoryLevelUnlocked(levelId, progress = loadStoryProgress()) {
    const id = Number(levelId);

    if (!Number.isFinite(id) || id < 1 || id > getTotalStoryLevels()) {
        return false;
    }

    if (id === 1) {
        return true;
    }

    const normalized = normalizeStoryProgress(progress);

    if (isUnderworldLevel(id)) {
        return isUnderworldStoryLevelUnlocked(id, normalized);
    }

    if (isStoryHardModeActive(normalized)) {
        const hardHighest = deriveHighestHardModeUnlockedLevelId(
            normalized.hardModeCompletedLevelIds
        );

        return (
            isStoryHardModeLevelCompleted(id - 1, normalized) ||
            isStoryHardModeLevelCompleted(id, normalized) ||
            id <= hardHighest
        );
    }

    return (
        isStoryLevelCompleted(id - 1, normalized) ||
        isStoryLevelCompleted(id, normalized) ||
        id <= normalized.highestUnlockedLevelId
    );
}

export function completeStoryLevel(
    levelId,
    progress = loadStoryProgress(),
    { hardMode = false } = {}
) {
    const id = Number(levelId);
    const level = getStoryLevelById(id);

    if (!level) {
        return normalizeStoryProgress(progress);
    }

    const normalized = normalizeStoryProgress(progress);
    const completedLevelIds = uniqueSortedIds([
        ...normalized.completedLevelIds,
        id,
    ]);
    const hardModeCompletedLevelIds =
        hardMode || isStoryHardModeActive(normalized)
            ? uniqueSortedIds([...normalized.hardModeCompletedLevelIds, id])
            : normalized.hardModeCompletedLevelIds;

    return saveStoryProgress({
        ...normalized,
        completedLevelIds,
        hardModeCompletedLevelIds,
        storyCompleted:
            normalized.storyCompleted || id === getStoryMainLevelCount(),
        highestUnlockedLevelId: deriveHighestUnlockedLevelId(completedLevelIds),
    });
}

export function isUnderworldUnlocked(progress = loadStoryProgress()) {
    const normalized = normalizeStoryProgress(progress);

    if (isStoryHardModeActive(normalized)) {
        return isStoryHardModeLevelCompleted(getStoryMainLevelCount(), normalized);
    }

    return hasCompletedStoryLevel(getStoryMainLevelCount(), progress);
}

export function isUnderworldCompleted(progress = loadStoryProgress()) {
    return hasCompletedStoryLevel(getTotalStoryLevels(), progress);
}

/** Story level that must be cleared before Boosts appear in runs. */
export const BOOST_UNLOCK_STORY_LEVEL = 4;

export function isStoryBoostFeatureUnlocked(progress = loadStoryProgress()) {
    return hasCompletedStoryLevel(BOOST_UNLOCK_STORY_LEVEL, progress);
}

export function hasCompletedStoryLevel(
    levelId,
    progress = loadStoryProgress()
) {
    return isStoryLevelCompleted(levelId, progress);
}

/** Story level that must be cleared before Endless Mode is available. */
export const ENDLESS_UNLOCK_STORY_LEVEL = 3;

/** Story level that must be cleared before the Store / heart packs unlock. */
export const STORE_UNLOCK_STORY_LEVEL = 2;

export function isEndlessModeUnlocked(progress = loadStoryProgress()) {
    return hasCompletedStoryLevel(ENDLESS_UNLOCK_STORY_LEVEL, progress);
}

export function isStoreUnlocked(progress = loadStoryProgress()) {
    return hasCompletedStoryLevel(STORE_UNLOCK_STORY_LEVEL, progress);
}

export function getStoryLevelLockHint(levelId, progress = loadStoryProgress()) {
    const id = Number(levelId);
    const normalized = normalizeStoryProgress(progress);
    const hardModeActive = isStoryHardModeActive(normalized);
    const levelLabel = hardModeActive ? "Hard Mode Story Level" : "Story Level";

    if (!Number.isFinite(id) || id < 1) {
        return "This Story level is locked.";
    }

    if (isUnderworldLevel(id) && !isUnderworldUnlocked(progress)) {
        return `Clear ${levelLabel} ${getStoryMainLevelCount()} to unlock the Underworld.`;
    }

    if (id === 16 || id === 17 || id === 18) {
        return `Clear ${levelLabel} 15 to unlock.`;
    }

    if (id === 19) {
        return `Clear ${levelLabel}s 16, 17, and 18 to unlock.`;
    }

    if (id === 1) {
        return "This Story level is locked.";
    }

    return `Clear ${levelLabel} ${id - 1} to unlock.`;
}

export function isEndlessBoardSizeUnlocked(
    boardSize,
    progress = loadStoryProgress()
) {
    const size = Number(boardSize);
    const normalized = normalizeStoryProgress(progress);

    if (size === 4) {
        // Endless 2×2 unlocks after Story World 1 basics (Level 3).
        return hasCompletedStoryLevel(3, normalized);
    }

    if (size === 9) {
        return hasCompletedStoryLevel(4, normalized);
    }

    if (size === 16) {
        return hasCompletedStoryLevel(9, normalized);
    }

    return false;
}

export function isModifierUnlocked(modifierKey, progress = loadStoryProgress()) {
    const normalized = normalizeStoryProgress(progress);
    const bonus = new Set(normalized.bonusModifiers || []);

    switch (modifierKey) {
        case "twistedMode":
            return (
                hasCompletedStoryLevel(TWISTED_UNLOCK_STORY_LEVEL, normalized) ||
                bonus.has("twistedMode")
            );
        case "shyCorgisMode":
            return (
                hasCompletedStoryLevel(SHY_UNLOCK_STORY_LEVEL, normalized) ||
                bonus.has("shyCorgisMode")
            );
        case "anxiousMode":
            return (
                hasCompletedStoryLevel(ANXIOUS_UNLOCK_STORY_LEVEL, normalized) ||
                bonus.has("anxiousMode")
            );
        case "blitzMode":
            return (
                normalized.storyCompleted ||
                hasCompletedStoryLevel(getStoryMainLevelCount(), normalized) ||
                bonus.has("blitzMode") ||
                normalized.legacyAlwaysOnModifiers
            );
        case "hardcoreMode":
            return (
                hasCompletedStoryLevel(HARDCORE_UNLOCK_STORY_LEVEL, normalized) ||
                bonus.has("hardcoreMode") ||
                normalized.legacyAlwaysOnModifiers
            );
        default:
            return false;
    }
}

export function isStoryHardModeUnlocked(progress = loadStoryProgress()) {
    const normalized = normalizeStoryProgress(progress);
    return Boolean(normalized.storyCompleted);
}

export function isStoryHardModeActive(progress = loadStoryProgress()) {
    const normalized = normalizeStoryProgress(progress);
    return Boolean(normalized.storyCompleted && normalized.hardModeEnabled);
}

export function setStoryHardModeEnabled(
    enabled,
    progress = loadStoryProgress()
) {
    const normalized = normalizeStoryProgress(progress);
    return saveStoryProgress({
        ...normalized,
        hardModeEnabled: Boolean(enabled) && Boolean(normalized.storyCompleted),
    });
}

export function isFullCustomEndlessUnlocked(
    progress = loadStoryProgress()
) {
    const normalized = normalizeStoryProgress(progress);
    return (
        normalized.storyCompleted || hasCompletedStoryLevel(12, normalized)
    );
}

export function getUnlockRequirementText(optionKey) {
    switch (optionKey) {
        case "board4":
            return "Clear Story Level 3 to unlock.";
        case "board9":
            return "Clear Story Level 4 to unlock.";
        case "board16":
            return "Clear Story Level 9 to unlock.";
        case "twistedMode":
            return `Clear Story Level ${TWISTED_UNLOCK_STORY_LEVEL} to unlock.`;
        case "shyCorgisMode":
            return `Clear Story Level ${SHY_UNLOCK_STORY_LEVEL} to unlock.`;
        case "anxiousMode":
            return `Clear Story Level ${ANXIOUS_UNLOCK_STORY_LEVEL} to unlock.`;
        case "blitzMode":
            return "Clear Story Mode to unlock.";
        case "hardcoreMode":
            return `Clear Story Level ${HARDCORE_UNLOCK_STORY_LEVEL} to unlock.`;
        default:
            return "Locked.";
    }
}

export function isEndlessHighScoreUnlocked(progress = loadStoryProgress()) {
    return hasCompletedStoryLevel(ENDLESS_UNLOCK_STORY_LEVEL, progress);
}

/**
 * Player-facing unlock toasts for completing a Story level.
 * Only includes newly granted Endless features / next Story access.
 */
export function getStoryUnlockToastMessages(levelId) {
    const id = Number(levelId);
    const messages = [];

    if (!Number.isFinite(id)) {
        return messages;
    }

    if (id < getTotalStoryLevels()) {
        messages.push(`Story Level ${id + 1} unlocked`);
    }

    switch (id) {
        case 2:
            messages.push("Unlocked the Store");
            break;
        case 3:
            messages.push("Unlocked Endless 2×2");
            messages.push("Unlocked Endless High Score tracking");
            break;
        case 4:
            messages.push("Unlocked Endless 3×3");
            messages.push("Unlocked Boosts");
            break;
        case 8:
            messages.push("Unlocked Hardcore for Endless");
            break;
        case 9:
            messages.push("Unlocked Endless 4×4");
            break;
        case 12:
            messages.push("Story complete — full custom Endless unlocked");
            messages.push("Unlocked Blitz Timer for Endless");
            messages.push("Unlocked Hard Mode");
            messages.push("Unlocked World Journey frame");
            messages.push("The Underworld awaits");
            break;
        case 13:
            messages.push("Unlocked Twisted Mode for Endless");
            break;
        case 14:
            messages.push("Unlocked Shy Corgis for Endless");
            break;
        case 15:
            messages.push("Unlocked Anxious for Endless");
            break;
        case 19:
            messages.push("Underworld complete!");
            messages.push("Unlocked Underworld Journey frame");
            break;
        default:
            break;
    }

    return messages;
}

export function getStoryProgressSummary(progress = loadStoryProgress()) {
    const normalized = normalizeStoryProgress(progress);
    const mainLevelCount = getStoryMainLevelCount();
    const bonusLevelCount = Math.max(0, getTotalStoryLevels() - mainLevelCount);
    const mainCompletedIds = normalized.completedLevelIds.filter(
        (id) => id <= mainLevelCount
    );
    const bonusCompletedIds = normalized.completedLevelIds.filter(
        (id) => id > mainLevelCount
    );
    const hardMainCompletedIds = normalized.hardModeCompletedLevelIds.filter(
        (id) => id <= mainLevelCount
    );
    const hardBonusCompletedIds = normalized.hardModeCompletedLevelIds.filter(
        (id) => id > mainLevelCount
    );
    const hardModeActive = isStoryHardModeActive(normalized);

    return {
        ...normalized,
        completedCount: normalized.completedLevelIds.length,
        hardModeCompletedCount: normalized.hardModeCompletedLevelIds.length,
        totalLevels: getTotalStoryLevels(),
        mainLevelCount,
        mainCompletedCount: mainCompletedIds.length,
        bonusLevelCount,
        bonusCompletedCount: bonusCompletedIds.length,
        hardMainCompletedCount: hardMainCompletedIds.length,
        hardBonusCompletedCount: hardBonusCompletedIds.length,
        hardModeActive,
        underworldUnlocked: isUnderworldUnlocked(normalized),
        underworldCompleted: isUnderworldCompleted(normalized),
    };
}

export function resetStoryProgressForTests() {
    try {
        window.localStorage.removeItem(LS_STORY_PROGRESS_KEY);
    } catch {
        // ignore
    }
}
